import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, BarChart3, TrendingUp, Package, MapPin, Layers, Palette, Frame, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#b08455', '#6b5b4f', '#8b7355', '#a0522d', '#4a3728', '#c4956a', '#7b6b5f', '#d4a574'];

const Bar = ({ label, value, max, color, amount }) => (
  <div className="flex items-center gap-3" data-testid={`bar-${label}`}>
    <span className="font-sans text-xs text-muted-foreground w-16 text-right shrink-0 truncate">{label}</span>
    <div className="flex-1 h-7 bg-muted/50 rounded-sm overflow-hidden relative">
      <div className="h-full rounded-sm transition-all duration-700" style={{ width: `${max ? (value / max * 100) : 0}%`, backgroundColor: color }} />
      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-sans font-medium text-foreground/70">
        {value} {amount ? `(${amount})` : ''}
      </span>
    </div>
  </div>
);

const Donut = ({ data, title }) => {
  const total = data.reduce((s, d) => s + d.value, 0);
  let cumulative = 0;
  return (
    <div className="text-center" data-testid={`donut-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <svg viewBox="0 0 100 100" className="w-32 h-32 mx-auto">
        {data.map((d, i) => {
          const pct = total ? d.value / total : 0;
          const startAngle = cumulative * 360;
          const endAngle = (cumulative + pct) * 360;
          cumulative += pct;
          const r = 35;
          const cx = 50, cy = 50;
          const start = polarToCartesian(cx, cy, r, endAngle);
          const end = polarToCartesian(cx, cy, r, startAngle);
          const largeArc = pct > 0.5 ? 1 : 0;
          const path = `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y}`;
          return <path key={i} d={path} fill="none" stroke={COLORS[i % COLORS.length]} strokeWidth="12" />;
        })}
        <text x="50" y="48" textAnchor="middle" className="fill-foreground" fontSize="12" fontWeight="600">{total}</text>
        <text x="50" y="60" textAnchor="middle" className="fill-muted-foreground" fontSize="6">orders</text>
      </svg>
      <p className="font-sans text-xs font-medium mt-1">{title}</p>
      <div className="flex flex-wrap justify-center gap-x-3 gap-y-1 mt-2">
        {data.map((d, i) => (
          <span key={i} className="flex items-center gap-1 text-[10px] text-muted-foreground">
            <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
            {d.label} ({d.value})
          </span>
        ))}
      </div>
    </div>
  );
};

function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = (angleDeg - 90) * Math.PI / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

const PrintAnalytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(90);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    setLoading(true);
    axios.get(`${API}/analytics/print-orders?days=${days}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setData(res.data))
      .catch(() => toast.error('Failed to load analytics'))
      .finally(() => setLoading(false));
  }, [days, navigate]);

  const fmt = (cents) => {
    if (!cents) return '0';
    return `${(cents / 100).toLocaleString('en-IN', { minimumFractionDigits: 0 })}`;
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" data-testid="print-analytics-loading">
      <Loader2 className="animate-spin text-accent" size={32} />
    </div>
  );

  if (!data) return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="text-muted-foreground">No data available</p>
    </div>
  );

  const sizeMax = Math.max(...Object.values(data.by_size).map(v => v.count), 1);
  const paperData = Object.entries(data.by_paper).map(([k, v]) => ({ label: k, value: v.count }));
  const frameData = Object.entries(data.by_frame).map(([k, v]) => ({ label: k || 'none', value: v.count }));

  return (
    <div className="min-h-screen bg-background pt-6 pb-16 px-4 sm:px-6 lg:px-8" data-testid="print-analytics-page">
      {/* Header */}
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin')} data-testid="back-btn">
              <ArrowLeft size={18} />
            </Button>
            <div>
              <h1 className="font-serif text-2xl sm:text-3xl font-light" data-testid="analytics-title">Print Order Analytics</h1>
              <p className="font-sans text-xs text-muted-foreground mt-0.5">{data.total_orders} total orders</p>
            </div>
          </div>
          <div className="flex gap-1.5">
            {[30, 90, 365].map(d => (
              <Button key={d} variant={days === d ? 'default' : 'outline'} size="sm" onClick={() => setDays(d)} data-testid={`period-${d}`}>
                {d === 365 ? '1Y' : `${d}D`}
              </Button>
            ))}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="summary-cards">
          <div className="bg-card rounded-lg p-4 border border-border">
            <div className="flex items-center gap-2 mb-1">
              <Package size={14} className="text-accent" />
              <span className="font-sans text-[10px] uppercase tracking-wider text-muted-foreground">Total Orders</span>
            </div>
            <p className="font-serif text-2xl font-light" data-testid="total-orders">{data.total_orders}</p>
          </div>
          <div className="bg-card rounded-lg p-4 border border-border">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp size={14} className="text-green-600" />
              <span className="font-sans text-[10px] uppercase tracking-wider text-muted-foreground">Total Revenue</span>
            </div>
            <p className="font-serif text-2xl font-light" data-testid="total-revenue">{fmt(data.total_revenue)}</p>
          </div>
          <div className="bg-card rounded-lg p-4 border border-border">
            <div className="flex items-center gap-2 mb-1">
              <MapPin size={14} className="text-blue-500" />
              <span className="font-sans text-[10px] uppercase tracking-wider text-muted-foreground">India</span>
            </div>
            <p className="font-serif text-2xl font-light">{data.by_region?.india?.count || 0}</p>
            <p className="font-sans text-[10px] text-muted-foreground">{fmt(data.by_region?.india?.revenue)} rev</p>
          </div>
          <div className="bg-card rounded-lg p-4 border border-border">
            <div className="flex items-center gap-2 mb-1">
              <MapPin size={14} className="text-violet-500" />
              <span className="font-sans text-[10px] uppercase tracking-wider text-muted-foreground">International</span>
            </div>
            <p className="font-serif text-2xl font-light">{data.by_region?.international?.count || 0}</p>
            <p className="font-sans text-[10px] text-muted-foreground">{fmt(data.by_region?.international?.revenue)} rev</p>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid lg:grid-cols-3 gap-8 mb-10">
          {/* Sales by Size */}
          <div className="lg:col-span-2 bg-card rounded-lg border border-border p-5" data-testid="size-chart">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 size={14} className="text-accent" />
              <h2 className="font-sans text-sm font-medium">Sales by Print Size</h2>
            </div>
            <div className="space-y-2">
              {Object.entries(data.by_size)
                .sort(([, a], [, b]) => b.count - a.count)
                .map(([size, v], i) => (
                  <Bar key={size} label={size} value={v.count} max={sizeMax} color={COLORS[i % COLORS.length]} amount={fmt(v.revenue)} />
                ))}
              {Object.keys(data.by_size).length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-6">No print orders yet</p>
              )}
            </div>
          </div>

          {/* Paper & Frame Donuts */}
          <div className="space-y-8">
            <div className="bg-card rounded-lg border border-border p-5" data-testid="paper-chart">
              <div className="flex items-center gap-2 mb-3">
                <Palette size={14} className="text-accent" />
                <h2 className="font-sans text-sm font-medium">By Paper Type</h2>
              </div>
              {paperData.length > 0 ? <Donut data={paperData} title="Paper" /> : <p className="text-sm text-muted-foreground text-center py-4">No data</p>}
            </div>
            <div className="bg-card rounded-lg border border-border p-5" data-testid="frame-chart">
              <div className="flex items-center gap-2 mb-3">
                <Frame size={14} className="text-accent" />
                <h2 className="font-sans text-sm font-medium">By Frame</h2>
              </div>
              {frameData.length > 0 ? <Donut data={frameData} title="Frame" /> : <p className="text-sm text-muted-foreground text-center py-4">No data</p>}
            </div>
          </div>
        </div>

        {/* Top Artworks */}
        {data.top_artworks?.length > 0 && (
          <div className="bg-card rounded-lg border border-border p-5 mb-10" data-testid="top-artworks">
            <div className="flex items-center gap-2 mb-4">
              <Layers size={14} className="text-accent" />
              <h2 className="font-sans text-sm font-medium">Top Selling Artworks (Prints)</h2>
            </div>
            <div className="space-y-2">
              {data.top_artworks.map((art, i) => (
                <div key={i} className="flex items-center justify-between py-2 px-3 bg-muted/30 rounded" data-testid={`top-artwork-${i}`}>
                  <div className="flex items-center gap-3">
                    <span className="font-serif text-lg font-light text-muted-foreground w-6">#{i + 1}</span>
                    <span className="font-sans text-sm">{art.title}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-sans text-xs text-muted-foreground">{art.count} orders</span>
                    <span className="font-sans text-xs font-medium">{fmt(art.revenue)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Fulfillment Status */}
        {Object.keys(data.by_status).length > 0 && (
          <div className="bg-card rounded-lg border border-border p-5" data-testid="status-breakdown">
            <h2 className="font-sans text-sm font-medium mb-3">Fulfillment Status</h2>
            <div className="flex flex-wrap gap-3">
              {Object.entries(data.by_status).map(([status, count]) => (
                <div key={status} className="px-3 py-1.5 bg-muted/40 rounded-full">
                  <span className="font-sans text-xs">
                    <span className="font-medium">{count}</span>
                    <span className="text-muted-foreground ml-1">{status.replace(/_/g, ' ')}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrintAnalytics;
